import { useMemo } from "react";
import { Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";
import {
  RP_LANDMARK_SOURCE,
  SIMULATION_REP_TIME_SOURCE,
  muscleName,
  simulateProgram,
  type Exercise,
  type Program,
  type SimulatedMuscle,
  type UnitsPreference,
} from "@heraion/shared";

import { Icon } from "../Icon/Icon";
import { joinList } from "../joinList";
import { Eyebrow } from "../primitives/Eyebrow";
import { useDisclosure } from "../primitives/useDisclosure";
import { useLocale } from "../primitives/useLocale";
import type { UiTheme } from "../theme";
import { useBreakpoint } from "../useBreakpoint";
import { SimulationDays } from "./SimulationDays";
import { SimulationFacts } from "./SimulationFacts";
import { SimulationMuscleRow } from "./SimulationMuscleRow";
import { formatSets } from "./simulationShared";
import { makeSimulationStyles } from "./simulationStyles";

const outOfBand = (m: SimulatedMuscle) =>
  m.setsPerWeek > 0 && (m.zone === "below-mev" || m.zone === "above-mrv");

// #355: the simulator in the program preview (Home's program card and the
// Library). The program as written, on an athlete with no history, read with
// the same rules as Progress. Closed, one line says how many muscles fall out
// of the band and how long a session lasts; open, the muscles against the
// band, then the distributions, the days, and the assumptions in plain words.
// Display only: nothing here writes.
export function ProgramSimulationSection({
  program,
  catalog,
  resolveName,
  unit,
  theme,
}: {
  program: Program;
  catalog: Exercise[] | null;
  resolveName: (slug: string) => string;
  unit: UnitsPreference;
  theme: UiTheme;
}) {
  const { t } = useTranslation();
  const lang = useLocale();
  const { wide } = useBreakpoint();
  const styles = makeSimulationStyles(theme);
  const open = useDisclosure(false);
  const all = useDisclosure(false);
  const sim = useMemo(() => (catalog ? simulateProgram(program, catalog) : null), [program, catalog]);
  if (!sim) return null;

  const days = new Map(sim.frequency.muscles.map((f) => [f.key, f.daysPerWeek]));
  const byName = (a: SimulatedMuscle, b: SimulatedMuscle) =>
    muscleName(a.muscle, lang).localeCompare(muscleName(b.muscle, lang));
  const out = sim.muscles
    .filter(outOfBand)
    .sort((a, b) => (a.zone === b.zone ? byName(a, b) : a.zone === "below-mev" ? -1 : 1));
  const rest = sim.muscles.filter((m) => m.setsPerWeek > 0 && !outOfBand(m)).sort(byName);
  const notTrained = sim.muscles.filter((m) => m.direction === "not-trained").sort(byName);
  const shown = all.expanded ? [...out, ...rest] : out;
  const seconds = sim.days.flatMap((d) => d.weeks.map((w) => w.seconds));
  const avgMin = seconds.length ? Math.round(seconds.reduce((a, b) => a + b, 0) / seconds.length / 60) : null;

  const title = t("simulator.title", { defaultValue: "Simulation" });
  const summary = [
    out.length > 0
      ? t("simulator.summaryOut", { count: out.length, defaultValue: "{{count}} muscles out of the band" })
      : t("simulator.summaryInBand", { defaultValue: "Every trained muscle in the band" }),
    avgMin ? t("simulator.summaryMinutes", { n: avgMin, defaultValue: "~{{n}} min a session" }) : null,
  ]
    .filter(Boolean)
    .join(" · ");
  const a = sim.assumptions;
  const source =
    a.secondsPerRepSource === SIMULATION_REP_TIME_SOURCE
      ? t("simulator.source.acsm", { defaultValue: "ACSM 2009, moderate tempo" })
      : a.secondsPerRepSource;

  return (
    <View style={styles.section}>
      <Pressable
        onPress={open.toggle}
        {...open.a11yProps}
        accessibilityRole="button"
        accessibilityLabel={`${title}. ${summary}`}
        style={styles.head}
      >
        <View style={styles.headText}>
          <View style={styles.titleRow}>
            <Eyebrow theme={theme}>{title}</Eyebrow>
            <Text style={styles.quiet}>
              {t("simulator.subtitle", { defaultValue: "the program as written, no history" })}
            </Text>
          </View>
          <Text style={styles.summary}>{summary}</Text>
        </View>
        <Icon
          name={open.expanded ? "chevron-up" : "chevron-down"}
          size={theme.typography.sizes.md}
          color={theme.palette.ink2}
        />
      </Pressable>
      {open.expanded ? (
        <>
          {sim.roundIsNotWeek ? (
            <Text style={styles.note}>
              {t("simulator.roundIsNotWeek", {
                count: sim.sessionsPerRound,
                week: sim.sessionsPerWeek,
                defaultValue:
                  "One round of this cycle is {{count}} sessions and it asks for {{week}} a week: every «a week» here means one round.",
              })}
            </Text>
          ) : null}
          <View style={[styles.columns, wide ? styles.columnsWide : null]}>
            <View style={[styles.column, wide ? styles.columnWide : null]}>
              <View>
                <Eyebrow theme={theme}>
                  {t("simulator.musclesTitle", { defaultValue: "Hard sets a week, cycle average" })}
                </Eyebrow>
                {shown.map((m, i) => (
                  <SimulationMuscleRow
                    key={m.muscle}
                    theme={theme}
                    styles={styles}
                    muscle={m}
                    days={days.get(m.muscle) ?? 0}
                    lang={lang}
                    divider={i > 0}
                  />
                ))}
                {rest.length > 0 ? (
                  <Pressable onPress={all.toggle} {...all.a11yProps} accessibilityRole="button" style={styles.seeAll}>
                    <Text style={styles.seeAllTxt}>
                      {all.expanded
                        ? t("progress.coverageShowFewer", { defaultValue: "Show fewer" })
                        : t("progress.coverageSeeAll", { count: rest.length, defaultValue: "See all muscles (+{{count}})" })}
                    </Text>
                  </Pressable>
                ) : null}
              </View>
              {notTrained.length > 0 ? (
                <Text style={styles.note}>
                  {t("simulator.notTrained", {
                    names: joinList(notTrained.map((m) => muscleName(m.muscle, lang)), lang),
                    defaultValue: "Not trained: {{names}}",
                  })}
                </Text>
              ) : null}
              {sim.hardSets.assumed > 0 ? (
                <Text style={styles.note}>
                  {t("simulator.assumedTotal", {
                    count: sim.hardSets.total,
                    assumed: sim.hardSets.assumed,
                    rir: a.hardSetRir,
                    defaultValue:
                      "{{assumed}} of {{count}} hard sets have no RPE, RIR or stop rule: they are counted hard (RIR {{rir}} or less).",
                  })}
                </Text>
              ) : null}
            </View>
            <View style={[styles.column, wide ? styles.columnWide : null]}>
              <SimulationFacts theme={theme} styles={styles} sim={sim} locale={lang} />
              <SimulationDays
                theme={theme}
                styles={styles}
                sim={sim}
                program={program}
                resolveName={resolveName}
                unit={unit}
                lang={lang}
              />
            </View>
          </View>
          {sim.unknownExercises.length > 0 ? (
            <Text style={styles.note}>
              {t("simulator.unknown", {
                count: sim.unknownExercises.length,
                names: joinList(sim.unknownExercises.map((s) => resolveName(s)), lang),
                defaultValue: "{{count}} exercises are not in the catalog and count for nothing: {{names}}",
              })}
            </Text>
          ) : null}
          <Text style={styles.quiet}>
            {t("simulator.assumptions", {
              rir: a.hardSetRir,
              band: a.landmarkSource === RP_LANDMARK_SOURCE.id ? t(RP_LANDMARK_SOURCE.labelKey) : a.landmarkSource,
              secs: formatSets(a.secondsPerRep, lang),
              source,
              rest: a.restFallbackSec,
              superset: a.supersetRestSec,
              defaultValue:
                "Directions, not forecasts: the program as written, on an athlete with no history. Hard set: RIR {{rir}} or less. Band: {{band}}. One rep: {{secs}} s ({{source}}). Rest not written: {{rest}} s, {{superset}} s in a superset.",
            })}
          </Text>
        </>
      ) : null}
    </View>
  );
}
